import { MODEL_CARDS, MODELS_FAQ, MODELS_SORA_MIGRATE } from '../../content/productModelsPage';
import { PageFaqSection } from '../ds';
import { CDN } from '../../lib/cdn';
import { ProductFinalCta } from './ProductFinalCta';
import { ProductHeroHeader } from './ProductHeroHeader';
import { ProductMigrate } from './ProductMigrate';

export function ProductModelsSections() {
  return (
    <div className="product-page">
      <ProductHeroHeader
        label="Product - AI Models"
        title={
          <>
            Every Frontier Model.
            <br />
            <em>One Reasoning Engine.</em>
          </>
        }
        sub="Induce orchestrates best-in-class video models inside Rhapsody. The models render the frames — Rhapsody plans, routes, and verifies every shot against the story."
        note="Kling 3.0 · Seedance 2.0 · Veo 3.1 · Nano Banana 2 · Auto-routing · Verified output"
        still={{
          kind: 'final_cut',
          slug: 'MODEL / ROUTING',
          scene: 'MODELS / 01',
          genre: 'ORCHESTRATION',
          sid: 'models-hero',
          alt: 'Induce orchestrating multiple AI video generation models behind a script-first pipeline',
          videoSrc: CDN.videos.direction,
        }}
      />

      <section className="answer band-light" aria-labelledby="m-aeo-h" id="model-strategy">
        <div className="container">
          <div className="content-measure">
          <div className="tag">- The model strategy</div>
          <div className="answer-body">
            <h2 id="m-aeo-h" className="a-lead">
              <b>The race on visual quality has infinite budgets behind it.</b> Kling leads on motion and
              audio, Veo on 4K cinematic quality, Seedance on multi-shot narrative — and the leaderboard
              reshuffles every few months.
            </h2>
            <p className="a-body">
              Induce doesn&apos;t enter that race. It sits above it and grades the output. Rhapsody plans each
              shot from the story, routes it to the best model, and verifies it before it enters your cut.
            </p>
            <div className="a-stats reveal">
              <div className="a-stat">
                <b>Top models</b>
                <span>Orchestrated per project</span>
              </div>
              <div className="a-stat">
                <b>Auto</b>
                <span>Per-shot model routing</span>
              </div>
              <div className="a-stat">
                <b>0</b>
                <span>Single-model lock-in</span>
              </div>
              <div className="a-stat">
                <b>100%</b>
                <span>Of shots verified before the cut</span>
              </div>
            </div>
          </div>
          </div>
        </div>
      </section>

      <section className="band-dark" id="models">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- The power behind the layer</span>
            <h2>
              Frontier Video Models + Rhapsody. <em>Verified output.</em>
            </h2>
          </div>
          <div className="model-grid reveal">
            {MODEL_CARDS.map((card) => (
              <div key={card.name} className="model-card">
                <div className="m-badge">{card.badge}</div>
                <div className="m-name">{card.name}</div>
                <div className="m-version">{card.version}</div>
                <p className="m-desc">{card.desc}</p>
                <div className="m-tags">
                  {card.tags.map((tag) => (
                    <span key={tag} className="m-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band-light">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- How routing works</span>
            <h2>
              Every Shot Gets Read <em>Before It Gets Rendered.</em> And Checked After.
            </h2>
            <p className="sub">
              Routing is the plan half of Rhapsody’s loop. Verification is the other half.
            </p>
          </div>
          <div className="step-flow reveal">
            {[
              ['01', 'Content type classification', 'Each shot is classified by its primary content: dialogue, action, establishing, or insert.', 'Classification'],
              ['02', 'Quality tier and budget signal', 'Your project\'s tier sets the model floor; hero shots route to Veo 3.1 and Seedance 2.0.', 'Quality tier'],
              ['03', 'Audio and dialogue requirements', 'Shots with dialogue route to Kling 3.0 Omni or Seedance 2.0 for lip-sync and synchronized audio.', 'Audio routing'],
              ['04', 'Verification and re-planning', 'After render, the Coherence Verifier checks story causality against the Cinematic Constitution’s 50 film-grammar rules. Failures re-plan automatically, sometimes to a different model.', 'Verify'],
            ].map(([n, title, body, tag]) => (
              <div key={n} className="step-item">
                <div className="step-n">{n}</div>
                <div className="step-body">
                  <h3>{title}</h3>
                  <p>{body}</p>
                </div>
                <div className="step-tag">{tag}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="band-dark">
        <div className="container">
          <div className="sec-head reveal">
            <span className="label-mono">- Model-agnostic architecture</span>
            <h2>
              When Sora shut down, Induce pipelines <em>kept running.</em>
            </h2>
          </div>
          <div className="cap-grid reveal">
            {[
              ['The Sora precedent', 'A $1B model can shut down overnight.', 'OpenAI deprecated Sora on April 26, 2026. Teams that built on it as a single model lost their pipelines.'],
              ['The Induce approach', 'Model-agnostic by architecture, not by accident.', 'No project depends on one model. Routing substitutes automatically when a model goes down, and the Verifier keeps output consistent.'],
              ['Migration path', 'Migrate from any tool in an afternoon.', 'Upload your screenplay to Induce and produce a comparable, verified first cut in hours.'],
              ['Future-proofing', 'New models slot in. Old projects stay intact.', 'New models join the routing options as they reach production quality. Your Project Memory Graph doesn\'t change.'],
            ].map(([kicker, title, body]) => (
              <div key={title} className="cap-item">
                <div className="kicker">{kicker}</div>
                <h3>{title}</h3>
                <p>{body}</p>
              </div>
            ))}
          </div>
          <div className="highlight-box reveal section-stack">
            <div className="h-label">- For Sora users</div>
            <p>
              If you have active projects on Sora, <em>the API shuts down September 24, 2026.</em> Upload your
              screenplay to Induce now.
            </p>
          </div>
          <div className="sec-head reveal section-stack">
            <span className="label-mono">- Model capabilities compared</span>
            <h2>
              Kling vs Seedance vs Veo - <em>for filmmakers.</em>
            </h2>
          </div>
          <div className="vs-wrap reveal">
            <table className="vs-table">
              <colgroup>
                <col style={{ width: '22%' }} />
                <col style={{ width: '19%' }} />
                <col style={{ width: '20%' }} />
                <col style={{ width: '19%' }} />
                <col style={{ width: '20%' }} />
              </colgroup>
              <thead>
                <tr>
                  <th scope="col">Capability</th>
                  <th scope="col">
                    Kling 3.0<span className="role">Motion · Audio</span>
                  </th>
                  <th scope="col">
                    Seedance 2.0<span className="role">Narrative · Long-form</span>
                  </th>
                  <th scope="col">
                    Veo 3.1<span className="role">Quality · 4K</span>
                  </th>
                  <th scope="col">
                    Nano Banana 2<span className="role">Style · Expression</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['Native resolution', 'plain', '1080p', 'plain', '1080p–4K', 'dg', 'Native 4K', 'plain', '1080p'],
                  ['Native audio / dialogue', 'dg', '5-language lip-sync', 'dg', 'Sync audio/video', 'dg', '48kHz speech', 'dp', 'Partial'],
                  ['Multi-shot sequences', 'dg', '3–15 sec native', 'dg', '15–20 sec native', 'dp', 'Scene extension', 'dp', 'Single-shot native'],
                  ['Motion complexity', 'dg', 'Leading', 'dg', 'Strong', 'dp', 'Strong', 'dp', 'Stylized motion'],
                  ['Photorealistic quality', 'dp', 'Strong', 'dp', 'Strong', 'dg', 'Leading', 'do', 'Stylized, not photo'],
                  ['Best used for', 'plain', 'Dialogue, action, volume', 'plain', 'Narrative sequences, long-form', 'plain', 'Hero shots, establishing', 'plain', 'Music video, mood, style'],
                ].map(([row, ...cells]) => (
                  <tr key={row}>
                    <th scope="row">{row}</th>
                    {Array.from({ length: 4 }, (_, i) => (
                      <td key={i}>
                        <span className={cells[i * 2]}>{cells[i * 2 + 1]}</span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="vs-note">
            Comparison based on publicly available model specifications as of June 2026.
          </p>
        </div>
      </section>

      <PageFaqSection
        eyebrow="- Frequently asked"
        title={
          <>
            Model questions <em>answered directly.</em>
          </>
        }
        items={MODELS_FAQ}
      />

      <ProductMigrate {...MODELS_SORA_MIGRATE} tone="dark" />

      <ProductFinalCta
        tone="light"
        eyebrow="- Model orchestration"
        title={
          <>
            Best-in-class models,
            <br />
            <em>one verified pipeline.</em>
          </>
        }
        body="Upload a screenplay and let Rhapsody route every shot to the best engine — and verify every frame serves the story."
        ghostLabel="Talk to Sales"
        ghostIsSales
        note="All models available on every tier"
      />
    </div>
  );
}
